import { forwardRef, ForwardedRef, ReactElement } from 'react';
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  AutocompleteRenderInputParams,
} from '@mui/material';
import { TextField, TextFieldProps } from '../TextField/text-field';

const MAX_OPTIONS_WITHOUT_KEYBOARD = 10;

/**
 * Pure Autocomplete component - no form library dependencies.
 * Can be used with any form library or as a controlled component.
 *
 * **Features:**
 * - Single and multiple selection modes
 * - Async option loading with loading state
 * - Free solo mode for custom text input
 * - Custom option rendering
 * - Error states and validation
 * - Suppresses the virtual keyboard (inputMode="none") when there are 10 or fewer
 *   options and not in freeSolo mode — short lists need no type-to-filter
 * - Full MUI Autocomplete functionality
 * - Forward ref support
 * - Full accessibility support
 *
 * **Common Use Cases:**
 * - Searchable dropdowns
 * - Multi-select fields
 * - Async data fetching (reference tables, API lookups)
 * - Type-ahead search
 * - Tag input fields
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Autocomplete
 *   options={countries}
 *   label="Country"
 *   getOptionLabel={(option) => option.name}
 *   onChange={(event, value) => setCountry(value)}
 * />
 *
 * // Multiple selection
 * <Autocomplete
 *   multiple
 *   options={tags}
 *   label="Tags"
 *   getOptionLabel={(option) => option}
 *   value={selectedTags}
 *   onChange={(event, value) => setSelectedTags(value)}
 * />
 *
 * // With async loading
 * <Autocomplete
 *   loading={isLoading}
 *   options={searchResults}
 *   label="Search"
 *   getOptionLabel={(option) => option.label}
 *   loadingText="Searching..."
 * />
 *
 * // With error state
 * <Autocomplete
 *   options={options}
 *   label="Required Field"
 *   error={!value}
 *   helperText={!value ? "This field is required" : ""}
 *   required
 * />
 *
 * // Free solo mode (custom input)
 * <Autocomplete
 *   freeSolo
 *   options={suggestions}
 *   label="Custom Input"
 *   getOptionLabel={(option) => option}
 * />
 * ```
 */
export interface AutocompleteProps<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
> extends Omit<MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'> {
  /**
   * The label for the autocomplete field.
   */
  label: string;

  /**
   * If true, the autocomplete will display an error state.
   * Use in combination with helperText to show validation errors.
   * @default false
   */
  error?: boolean;

  /**
   * Helper text to display below the field.
   * Often used to show validation errors or helpful hints.
   */
  helperText?: string;

  /**
   * If true, the field will be marked as required with an asterisk.
   * @default false
   */
  required?: boolean;

  /**
   * Additional props to pass to the TextField component.
   * Note: slotProps.input and slotProps.htmlInput are merged with Autocomplete's params
   * to preserve the dropdown button and other Autocomplete-specific functionality.
   *
   * @example
   * ```tsx
   * textFieldProps={{
   *   placeholder: "Search...",
   *   variant: "filled",
   *   slotProps: {
   *     input: { startAdornment: <SearchIcon /> }
   *   }
   * }}
   * ```
   */
  textFieldProps?: Partial<TextFieldProps>;

  /**
   * Callback fired when related fields should be updated.
   * This allows parent components to sync related field values.
   *
   * **TreePlotter-specific feature:**
   * Used for synchronizing dependent form fields, such as:
   * - Auto-populating related fields from a reference table
   * - Syncing species common/latin names
   * - Updating child fields when parent field changes
   *
   * Note: This callback is not invoked by the Autocomplete component itself.
   * It's meant to be called by parent components/wrappers that implement
   * field synchronization logic.
   *
   * @param updates - Object mapping field identifiers to their new values
   *
   * @example
   * ```tsx
   * <Autocomplete
   *   options={referenceTableItems}
   *   label="Item"
   *   onRelatedChange={(updates) => {
   *     // Sync related fields
   *     setValue('relatedField1', updates.field1);
   *     setValue('relatedField2', updates.field2);
   *   }}
   * />
   * ```
   */
  onRelatedChange?: (updates: Record<string, unknown>) => void;
}

/**
 * Internal implementation component that handles the rendering logic.
 * Separated from the exported component to support proper generic type inference with forwardRef.
 */
function AutocompleteInner<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>(
  {
    onRelatedChange,
    label,
    error,
    helperText,
    required,
    textFieldProps,
    ...props
  }: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  ref: ForwardedRef<HTMLDivElement>
) {
  const suppressKeyboard = !props.freeSolo && props.options.length <= MAX_OPTIONS_WITHOUT_KEYBOARD;

  return (
    <MuiAutocomplete
      ref={ref}
      blurOnSelect
      renderInput={(params: AutocompleteRenderInputParams) => {
        // Merge input slot props: params.InputProps has Autocomplete's built-in adornments/ref.
        // Consumer customizations from textFieldProps.slotProps.input are merged on top.
        const consumerInputSlot = textFieldProps?.slotProps?.input as
          | { startAdornment?: React.ReactNode; endAdornment?: React.ReactNode }
          | undefined;

        const mergedInputSlot = {
          ...params.InputProps,
          ...consumerInputSlot,
          startAdornment: (
            <>
              {consumerInputSlot?.startAdornment}
              {params.InputProps?.startAdornment}
            </>
          ),
          endAdornment: (
            <>
              {params.InputProps?.endAdornment}
              {consumerInputSlot?.endAdornment}
            </>
          ),
        };

        const consumerHtmlInput = textFieldProps?.slotProps?.htmlInput as object | undefined;
        const mergedHtmlInput = {
          ...params.inputProps,
          ...(suppressKeyboard ? { inputMode: 'none' as const } : {}),
          ...consumerHtmlInput,
        };

        const { slotProps: _slotProps, ...restTextFieldProps } = textFieldProps ?? {};

        return (
          <TextField
            {...params}
            {...restTextFieldProps}
            label={label}
            error={error}
            helperText={helperText}
            required={required}
            fullWidth
            slotProps={{
              ..._slotProps,
              input: mergedInputSlot,
              htmlInput: mergedHtmlInput,
            }}
          />
        );
      }}
      {...props}
    />
  );
}

/**
 * Autocomplete component - A searchable select field that extends MUI Autocomplete.
 *
 * Export as a generic component with proper TypeScript support for all selection modes.
 * The complex type assertion allows proper generic type inference while using forwardRef.
 */
export const Autocomplete = forwardRef(AutocompleteInner) as (<
  T = unknown,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>(
  props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>
) => ReactElement) & {
  displayName?: string;
};

Autocomplete.displayName = 'Autocomplete';
